/**
 * Tax time (rd15 A9, owner-approved): the 1099 totals the monthly close
 * prepares, and the download that already exists.
 *
 * WHY THIS PAGE EXISTS. The owner asked for a Tax time screen that starts with
 * the 1099 totals download. The numbers are a *saved report window*
 * (`vendors-1099`), published by the month close (`pipeline/close.py` ->
 * `run_reporting_cycle`), and read through `GET .../report-windows`. There is
 * no CPA package here on purpose: the owner deferred it.
 *
 * WHAT IT SAYS WHEN THERE IS NOTHING. A company whose close has not run has no
 * window to list. An empty file would be a lie about a tax figure, so the page
 * says plainly that nothing is prepared yet and that the monthly close makes
 * it. That is the whole empty state, and it is not an error.
 *
 * ROLES. Reading a window is owner or account manager (`G1`), so both get this
 * page and both may download. Nothing here writes.
 */

import { ConsoleDataError, type AdminClient } from "../api.js";
import type { CompanySlug, StaffRole } from "../types.js";
import type {
  ReportWindowCatalogItem,
  ReportWindowDownload,
} from "../console/types.js";
import { clear, h } from "../dom.js";
import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import { dateText } from "../ui/display-words.js";
import { failureCopy } from "../ui/failure.js";
import { readerRole } from "../ui/facts.js";
import { saveCsv } from "../ui/csv-download.js";
import { pageHeader } from "../ui/components.js";
import { slugText } from "../console/report-render.js";
import { navLabel } from "../ui/sentences.js";

export interface TaxTimePageOptions {
  client: AdminClient;
  company: CompanySlug;
  root: HTMLElement;
  /** The role the shell already knows (decision 10: unknown reads as the AM). */
  role?: StaffRole;
  /** Return false if a newer navigate superseded this render; drop results. */
  isCurrent?: () => boolean;
}

/** The report identifier the pipeline publishes the 1099 totals under. */
const VENDORS_1099 = "vendors-1099";

/** What a saved window's slot is called, in plain words. */
function slotWords(item: ReportWindowCatalogItem): string {
  const from = item.windowStart === undefined ? "" : dateText(item.windowStart);
  const to = item.windowEnd === undefined ? "" : dateText(item.windowEnd);
  const span = from !== "" && to !== "" ? `${from} to ${to}` : "";
  const slot =
    item.typicalSlot === "last_year"
      ? "Last year"
      : item.typicalSlot === "ytd"
        ? "Year to date"
        : item.typicalSlot === "quarter"
          ? "The quarter so far"
          : item.typicalSlot === "month"
            ? "This month"
            : "Saved totals";
  return span === "" ? slot : `${slot} (${span})`;
}

/** The status of one saved window, and what the person can do about it. */
function statusWords(item: ReportWindowCatalogItem): string {
  if (item.status === "ready") {
    const made = item.generatedAt === undefined ? "" : dateText(item.generatedAt);
    return made === "" ? "Ready to download." : `Ready. Prepared ${made}.`;
  }
  if (item.status === "pending") {
    return "Not ready yet. The monthly close makes it.";
  }
  return "This one could not be prepared. Ask for it again at the next close.";
}

export async function renderTaxTimePage(opts: TaxTimePageOptions): Promise<void> {
  const { client, company, root } = opts;
  const role: StaffRole = readerRole(opts.role);
  const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;
  clear(root);

  const page = h("div", {
    class: "tax-time-page",
    "data-role": "tax-time-page",
    "data-viewer-role": role,
  });
  root.append(page);
  page.append(
    pageHeader(
      // The page's name comes from the registry; only the sentence is here.
      navLabel("tax-time"),
      "The 1099 totals for this company. Download the file your accountant asks for.",
    ),
  );

  const body = h("div", { "data-role": "tax-time-body" });
  page.append(body);
  body.append(h("p", { class: "muted", role: "status" }, ["Loading tax totals\u2026"]));

  /**
   * What is prepared, in one read. `listReportWindows` answers both the saved
   * "typical" windows and anything a person asked for, so there is one list.
   */
  async function paintList(): Promise<void> {
    let items: ReportWindowCatalogItem[];
    try {
      const catalog = await client.listReportWindows(company, VENDORS_1099);
      if (!stillCurrent()) return;
      items = catalog.items;
    } catch (error) {
      if (!stillCurrent()) return;
      clear(body);
      body.append(
        h("div", { class: "empty-state", role: "alert", "data-role": "tax-time-error" }, [
          h("p", {}, [readFailureSentence(error, role)]),
          retryButton(() => {
            void paintList();
          }),
        ]),
      );
      return;
    }
    clear(body);
    if (items.length === 0) {
      // A company whose month close has not run has nothing to list. An empty
      // CSV would claim "no 1099 payments", which is not what an unclosed month
      // means, so the page says which thing is missing and what makes it.
      body.append(
        h("div", { class: "empty-state", "data-role": "tax-time-empty" }, [
          h("p", {}, [
            "No tax totals are prepared for this company yet.",
          ]),
          h("p", { class: "muted" }, [
            "They are made when a month is closed, so they appear after the next month-end close.",
          ]),
        ]),
      );
      return;
    }
    const list = h("ul", { class: "tax-time-list", "data-role": "tax-time-list" });
    for (const item of items) list.append(windowRow(item));
    body.append(
      h("h2", { class: "gvf-section-title" }, ["1099 totals"]),
      list,
      h("p", { class: "muted", "data-role": "tax-time-note" }, [
        "These are the same totals the books use. The monthly close prepares them, so a month that is not closed has none.",
      ]),
    );
  }

  /** One saved window: what it covers, whether it is ready, and the download. */
  function windowRow(item: ReportWindowCatalogItem): HTMLElement {
    const row = h("li", {
      class: "tax-time-row",
      "data-role": "tax-time-row",
      "data-window-id": item.id,
      "data-status": item.status,
    });
    row.append(
      h("div", { class: "tax-time-main" }, [
        h("strong", {}, [slotWords(item)]),
        h("span", { class: "muted", "data-role": "tax-time-status" }, [
          ` ${statusWords(item)}`,
        ]),
      ]),
    );
    if (item.status === "ready") {
      const download = h(
        "button",
        {
          type: "button",
          class: "btn btn-primary",
          "data-action": "download-1099",
          "aria-label": `Download 1099 totals for ${slotWords(item)}`,
        },
        ["Download CSV"],
      );
      const problem = h("p", {
        class: "muted",
        "data-role": "tax-time-download-problem",
        role: "status",
      });
      problem.hidden = true;
      download.addEventListener("click", () => {
        if (download.disabled) return;
        void downloadWindow(item, download, problem);
      });
      row.append(download, problem);
    }
    return row;
  }

  async function downloadWindow(
    item: ReportWindowCatalogItem,
    button: HTMLButtonElement,
    problem: HTMLElement,
  ): Promise<void> {
    button.disabled = true;
    try {
      const downloaded: ReportWindowDownload = await client.getReportWindow(
        company,
        item.id,
      );
      if (!stillCurrent()) return;
      saveCsv(csvName(amVisibleDisplayText(downloaded.filename), item), downloaded.text);
      problem.hidden = true;
    } catch (error) {
      if (!stillCurrent()) return;
      problem.hidden = false;
      clear(problem);
      problem.append(readFailureSentence(error, role));
    } finally {
      button.disabled = false;
    }
  }

  /** The file's own name when the Worker gave one, else one that says the period. */
  function csvName(filename: string, item: ReportWindowCatalogItem): string {
    const clean = amVisibleDisplayText(filename).trim();
    if (clean.toLowerCase().endsWith(".csv")) return clean;
    // The slug and the window token are file-name keys, never words on screen.
    const slug = slugText(company);
    const token = amVisibleDisplayText(item.windowToken).replace(/[^a-z0-9-]+/gi, "-");
    return `1099-${slug}-${token}.csv`;
  }

  function readFailureSentence(error: unknown, reader: StaffRole): string {
    const status = error instanceof ConsoleDataError ? error.status : 0;
    if (status === 401 || status === 403 || status === 429) {
      return failureCopy(error, { role: reader, context: "read" }).sentence;
    }
    return "Tax totals could not load. Try again.";
  }

  function retryButton(onTry: () => void): HTMLButtonElement {
    const button = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost",
        "data-action": "retry",
        "aria-label": "Try again",
      },
      ["Try again"],
    );
    button.addEventListener("click", () => {
      onTry();
    });
    return button;
  }

  await paintList();
}
